import * as React from 'react';
import { createContext, useContext, useState } from 'react';
import type { FC, ReactNode } from 'react';

type DubbingProviderProps = {
  children: ReactNode;
};

type ShouldChangeTranscriptLanguageState = {
  shouldChange: boolean;
  languageCode: string | undefined;
};

type ShouldNudgeBeVisibleState = {
  isVisible: boolean;
  languageCode: string | undefined;
};

type ChangeSubtitleOnAudioChangeState = {
  shouldChange: boolean;
  languageCode: string | undefined;
};

type ShouldChangeTranscriptTracksState = {
  shouldChange: boolean;
  languageCode: string | undefined;
};

export type DubbingContextProps = {
  shouldChangeTranscriptTracks: ShouldChangeTranscriptTracksState;
  setShouldChangeTranscriptTracks: (shouldChangeTranscriptTracks: ShouldChangeTranscriptTracksState) => void;
  shouldChangeTranscriptLanguage: ShouldChangeTranscriptLanguageState;
  setShouldChangeTranscriptLanguage: (shouldChangeTranscriptLanguage: ShouldChangeTranscriptLanguageState) => void;
  reportIssueSectionVisible: boolean;
  setReportIssueSectionVisible: (reportIssueSectionVisible: boolean) => void;
  shouldNudgeBeVisible: ShouldNudgeBeVisibleState;
  setShouldNudgeBeVisible: (shouldNudgeBeVisible: ShouldNudgeBeVisibleState) => void;
  changeSubtitleOnAudioChange: ChangeSubtitleOnAudioChangeState;
  setChangeSubtitleOnAudioChange: (changeSubtitleOnAudioChange: ChangeSubtitleOnAudioChangeState) => void;
  isDubbingAvailable: boolean;
  setIsDubbingAvailable: (isDubbingAvailable: boolean) => void;
  defaultLanguageKey: string;
  setDefaultLanguageKey: (defaultLanguageKey: string) => void;
};

const initialState = {
  shouldChangeTranscriptTracks: {
    shouldChange: false,
    languageCode: undefined,
  },
  setShouldChangeTranscriptTracks: () => {},
  shouldChangeTranscriptLanguage: {
    shouldChange: false,
    languageCode: undefined,
  },
  setShouldChangeTranscriptLanguage: () => {},
  reportIssueSectionVisible: false,
  setReportIssueSectionVisible: () => {},
  shouldNudgeBeVisible: {
    isVisible: false,
    languageCode: undefined,
  },
  setShouldNudgeBeVisible: () => {},
  changeSubtitleOnAudioChange: {
    shouldChange: false,
    languageCode: undefined,
  },
  setChangeSubtitleOnAudioChange: () => {},
  isDubbingAvailable: false,
  setIsDubbingAvailable: () => {},
  defaultLanguageKey: 'en',
  setDefaultLanguageKey: () => {},
};

export const DubbingContext = createContext<DubbingContextProps>(initialState);

export const useDubbingContext = () => useContext(DubbingContext);

export const DubbingProvider: FC<DubbingProviderProps> = ({ children }) => {
  const [shouldChangeTranscriptTracks, setShouldChangeTranscriptTracks] = useState<ShouldChangeTranscriptTracksState>({
    shouldChange: false,
    languageCode: undefined,
  });
  const [defaultLanguageKey, setDefaultLanguageKey] = useState<string>('en');
  const [isDubbingAvailable, setIsDubbingAvailable] = useState<boolean>(false);
  const [shouldChangeTranscriptLanguage, setShouldChangeTranscriptLanguage] =
    useState<ShouldChangeTranscriptLanguageState>({
      shouldChange: false,
      languageCode: undefined,
    });

  const [shouldNudgeBeVisible, setShouldNudgeBeVisible] = useState<ShouldNudgeBeVisibleState>({
    isVisible: false,
    languageCode: undefined,
  });

  const [reportIssueSectionVisible, setReportIssueSectionVisible] = useState<boolean>(false);
  const [changeSubtitleOnAudioChange, setChangeSubtitleOnAudioChange] = useState<ChangeSubtitleOnAudioChangeState>({
    shouldChange: false,
    languageCode: undefined,
  });

  return (
    <DubbingContext.Provider
      value={{
        shouldChangeTranscriptTracks,
        setShouldChangeTranscriptTracks,
        shouldChangeTranscriptLanguage,
        setShouldChangeTranscriptLanguage,
        reportIssueSectionVisible,
        setReportIssueSectionVisible,
        shouldNudgeBeVisible,
        setShouldNudgeBeVisible,
        changeSubtitleOnAudioChange,
        setChangeSubtitleOnAudioChange,
        isDubbingAvailable,
        setIsDubbingAvailable,
        defaultLanguageKey,
        setDefaultLanguageKey,
      }}
    >
      {children}
    </DubbingContext.Provider>
  );
};
